import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Zap } from "lucide-react";

const PACKAGES = [
  { key: "stc_topup_50",  usd: 50,  bonus: 0 },
  { key: "stc_topup_100", usd: 100, bonus: 5 },
  { key: "stc_topup_250", usd: 250, bonus: 25 },
];

export function TopUp() {
  const [loading, setLoading] = useState(null);
  const buy = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/stripe/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errText(e)); setLoading(null); }
  };
  return (
    <div className="space-y-6" data-testid="topup-panel">
      <div>
        <h1 className="font-display text-3xl">Top Up Vault · Stripe</h1>
        <p className="text-white/60 text-sm mt-2">Pay with any card. Balance credits to your vault the moment the payment settles at Stripe.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {PACKAGES.map((p) => (
          <div key={p.key} className="panel p-6" data-testid={`pkg-${p.key}`}>
            <div className="chip gold mb-3">TOP-UP</div>
            <div className="font-display text-5xl text-[#d4af37]">${p.usd}</div>
            <div className="mt-3 text-sm text-white/70">Credits ${p.usd + p.bonus} to your vault {p.bonus > 0 && <span className="text-[#39ff14] font-mono">(+${p.bonus} bonus)</span>}</div>
            <button data-testid={`buy-${p.key}`} onClick={()=>buy(p.key)} disabled={loading===p.key} className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2">
              <Zap size={14}/>{loading===p.key ? "Redirecting…" : "Pay with Stripe →"}
            </button>
          </div>
        ))}
      </div>
      <div className="panel p-4 text-xs text-white/50 font-mono">
        Test card <span className="text-[#00f0ff]">4242 4242 4242 4242</span> · any future expiry · any CVC
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sid = sp.get("session_id");
  const [st, setSt] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sid) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/stripe/status/${sid}`);
        setSt(data);
        if (data.payment_status === "paid" || data.status === "failed" || data.status === "expired") {
          clearInterval(t);
        }
      } catch {}
      setTries((n)=>n+1);
    }, 2000);
    return () => clearInterval(t);
  }, [sid]);

  useEffect(() => { if (tries > 30) toast.info("Still confirming with Stripe…"); }, [tries]);

  return (
    <div className="min-h-screen grid place-items-center px-6 text-white">
      <div className="panel p-10 max-w-md text-center" data-testid="payment-success">
        {st?.payment_status === "paid" ? (
          <>
            <CheckCircle2 className="text-[#39ff14] mx-auto" size={56}/>
            <div className="font-display text-3xl mt-4">Payment settled</div>
            <div className="text-white/70 mt-2">${(st.amount/100).toFixed(2)} {st.currency?.toUpperCase()} credited to your vault.</div>
            <div className="font-mono text-[10px] text-white/40 mt-3">Session {sid?.slice(0,20)}…</div>
            <button onClick={()=>nav("/app")} className="btn-primary mt-6" data-testid="back-to-vault">Back to vault</button>
          </>
        ) : st?.status === "failed" || st?.status === "expired" ? (
          <>
            <XCircle className="text-[#ff3366] mx-auto" size={56}/>
            <div className="font-display text-3xl mt-4">Payment {st.status}</div>
            <button onClick={()=>nav("/app/topup")} className="btn-primary mt-6">Try again</button>
          </>
        ) : (
          <>
            <div className="animate-pulse text-[#d4af37] font-display text-2xl">Confirming with Stripe…</div>
            <div className="text-white/50 text-sm mt-3">This usually takes ~2 seconds.</div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center px-6 text-white">
      <div className="panel p-10 max-w-md text-center" data-testid="payment-cancel">
        <XCircle className="text-[#d4af37] mx-auto" size={56}/>
        <div className="font-display text-3xl mt-4">Payment cancelled</div>
        <div className="text-white/60 mt-2">No charge was made.</div>
        <button onClick={()=>nav("/app/topup")} className="btn-primary mt-6">Back to top-up</button>
      </div>
    </div>
  );
}
